// Built-in color palettes for the Themes v2 selector. This is the single source of the
// `palette:*` id strings; the color resolver, the selector UI, and the backend default stamp all
// reuse these exact ids verbatim.
//
// The palette list, order, swatch colors, and semantics come from the eight explicitly designed
// theme frames in Figma "Dashboard Themes v1" (node 2884:48415): "Palette 1"-"Palette 4" and
// "Theme 5"-"Theme 8". The theme frames — not the dropdown mock, whose swatches have drifted — are
// the source of truth. Seven also carry authored gradients (see paletteBackgrounds.ts); Coral's
// background is still derived.

export interface PaletteSemantics {
  // TEXT tokens for KPI deltas (contrast-nudged per mode at render time).
  text: { positive: string; negative: string };
  // CHART fill tokens, used verbatim. The design pins the chart negative to a 40% grey on every
  // palette; the chart positive is the palette's green/teal, or the primary when there is none.
  chart: { positive: string; negative: string };
  // Muted grey shared by text + charts.
  neutral: string;
}

export interface BuiltinPalette {
  id: string;
  name: string;
  // Ordered brand colors; colors[0] is the primary.
  colors: string[];
  // Designer-authored semantic tokens from the palette theme frames in Figma "Dashboard Themes v1"
  // ("Positive/Negative (Text on KPI Blocks)" and "Positive/Negative (Charts)" swatches).
  semantics: PaletteSemantics;
}

// The muted grey is constant across every palette in the design. Exported so custom themes
// (buildThemeFromSpec) pin the same greys when deriving semantics from brand colors.
export const PALETTE_NEUTRAL = "#999999";
// Chart "negative" swatch is the same 40% grey on all eight Figma theme frames.
export const PALETTE_CHART_NEGATIVE = "rgba(153,153,153,0.4)";
const NEUTRAL = PALETTE_NEUTRAL;
const CHART_NEGATIVE = PALETTE_CHART_NEGATIVE;

export const BUILTIN_PALETTES: BuiltinPalette[] = [
  {
    id: "palette:ocean",
    name: "Ocean",
    // Figma Theme 1 ("Palette 1").
    colors: ["#3053FF", "#00B7FF", "#4BE4FF", "#08C8A4", "#44E6C8"],
    semantics: {
      text: { positive: "#06A588", negative: "#E22326" },
      chart: { positive: "#06A588", negative: CHART_NEGATIVE },
      neutral: NEUTRAL,
    },
  },
  {
    id: "palette:berry",
    name: "Berry",
    // Figma Theme 2 ("Palette 2").
    colors: ["#9D064B", "#E31171", "#FF8044", "#FFC61B", "#FEE597"],
    semantics: {
      text: { positive: "#06989D", negative: "#D01369" },
      chart: { positive: "#06989D", negative: CHART_NEGATIVE },
      neutral: NEUTRAL,
    },
  },
  {
    id: "palette:candy",
    name: "Candy",
    // Figma Theme 3 ("Palette 3").
    colors: ["#5D00CF", "#C600D8", "#FF8F41", "#FFB700", "#FFD466"],
    semantics: {
      text: { positive: "#06989D", negative: "#D01369" },
      chart: { positive: "#06989D", negative: CHART_NEGATIVE },
      neutral: NEUTRAL,
    },
  },
  {
    id: "palette:reef",
    name: "Reef",
    // Figma Theme 30 ("Palette 4"): positive is the palette's own teal.
    colors: ["#FF6B36", "#FFA01C", "#FFBF69", "#30C4B6", "#5FD5C9"],
    semantics: {
      text: { positive: "#30C4B6", negative: "#FF3636" },
      chart: { positive: "#30C4B6", negative: CHART_NEGATIVE },
      neutral: NEUTRAL,
    },
  },
  {
    id: "palette:splash",
    name: "Splash",
    // Figma Theme 27 ("Theme 5").
    colors: ["#0091FF", "#00DAC2", "#6D7CFF", "#FF684E", "#FF9B49"],
    semantics: {
      text: { positive: "#00AC9A", negative: "#FF3636" },
      chart: { positive: "#00AC9A", negative: CHART_NEGATIVE },
      neutral: NEUTRAL,
    },
  },
  {
    id: "palette:dusk",
    name: "Dusk",
    // Figma Theme 21 ("Theme 6"): no green in the palette, so the chart positive is the primary
    // and the text negative is the palette's own coral.
    colors: ["#8C76CA", "#FFC677", "#FE6438", "#FF9576", "#B9A8EA"],
    semantics: {
      text: { positive: "#4EB781", negative: "#FE6438" },
      chart: { positive: "#8C76CA", negative: CHART_NEGATIVE },
      neutral: NEUTRAL,
    },
  },
  {
    id: "palette:sunrise",
    name: "Sunrise",
    // Figma Theme 15 ("Theme 7"): chart positive is the primary blue.
    colors: ["#0C6FF9", "#32AAFF", "#FFCD04", "#FF7D1D", "#FFC485"],
    semantics: {
      text: { positive: "#3FB643", negative: "#FF3636" },
      chart: { positive: "#0C6FF9", negative: CHART_NEGATIVE },
      neutral: NEUTRAL,
    },
  },
  {
    id: "palette:coral",
    name: "Coral",
    // Figma Theme 8: no green in the palette, so the chart positive is the primary and the text
    // negative is the palette's own red-orange.
    colors: ["#FF3700", "#FFAA00", "#FFCC00", "#FFE479", "#C22A00"],
    semantics: {
      text: { positive: "#3FB643", negative: "#FF3700" },
      chart: { positive: "#FF3700", negative: CHART_NEGATIVE },
      neutral: NEUTRAL,
    },
  },
];

export interface LegacyPaletteMatch {
  paletteId: string;
  mode: "light" | "dark";
  background: "on" | "off";
}

// Display-only, best-effort closest palette for each legacy theme id, so the selector can highlight
// a palette when a legacy dashboard is opened. `mode` mirrors `isDarkTheme` (agGrid browserColorScheme).
// Keep in sync with the legacy theme ids in themes.ts
export const LEGACY_THEME_TO_PALETTE: Record<string, LegacyPaletteMatch> = {
  light: { paletteId: "palette:ocean", mode: "light", background: "on" },
  dark: { paletteId: "palette:ocean", mode: "dark", background: "on" },
  ocean: { paletteId: "palette:ocean", mode: "light", background: "on" },
  slate: { paletteId: "palette:dusk", mode: "dark", background: "on" },
  midnight: { paletteId: "palette:splash", mode: "dark", background: "on" },
  mint: { paletteId: "palette:reef", mode: "light", background: "on" },
  warm: { paletteId: "palette:coral", mode: "light", background: "on" },
  honey: { paletteId: "palette:sunrise", mode: "light", background: "on" },
  corporate: { paletteId: "palette:ocean", mode: "light", background: "on" },
  ultraviolet: { paletteId: "palette:candy", mode: "light", background: "on" },
  "golden-harvest": { paletteId: "palette:sunrise", mode: "light", background: "on" },
  voxel: { paletteId: "palette:berry", mode: "dark", background: "on" },
  "netflix-brand": { paletteId: "palette:berry", mode: "dark", background: "on" },
  "miro-brand": { paletteId: "palette:sunrise", mode: "light", background: "on" },
  "klaviyo-brand": { paletteId: "palette:reef", mode: "dark", background: "on" },
  "richemont-brand": { paletteId: "palette:splash", mode: "light", background: "on" },
  "spotify-brand": { paletteId: "palette:reef", mode: "dark", background: "on" },
  "disney-brand": { paletteId: "palette:splash", mode: "light", background: "on" },
};
